import { calculateMedication, describeConcentration, type ProviderLevel } from '@ems/clinical';
import { BookOpen, Syringe } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { DoseCard } from '@/components/DoseCard';
import { Markdown } from '@/components/Markdown';
import { ClinicalDisclaimer, SampleContentNotice } from '@/components/notices';
import { PatientBar } from '@/components/PatientBar';
import { Alert, Badge, ButtonLink, Card, EmptyState, Spinner } from '@/components/ui';
import { useEffectiveProtocols } from '@/features/protocols/api';
import { useAuth } from '@/lib/auth';
import { cn, formatDate, LEVEL_LABELS } from '@/lib/utils';
import { usePatient } from '@/stores/patient';
import { useMedication } from './api';
import { MedicationBadges } from './MedsPage';

function InfoList({ title, items, tone }: { title: string; items: string[]; tone?: 'danger' | 'warning' }) {
  if (items.length === 0) return null;
  return (
    <Card
      className={cn(
        'p-4',
        tone === 'danger' && 'border-red-200 dark:border-red-500/30',
        tone === 'warning' && 'border-amber-200 dark:border-amber-500/30',
      )}
    >
      <h3 className={cn('mb-2 font-semibold', tone === 'danger' && 'text-red-700 dark:text-red-400', tone === 'warning' && 'text-amber-700 dark:text-amber-400')}>{title}</h3>
      <ul className="list-disc space-y-1 pl-5 text-sm">
        {items.map((i) => (
          <li key={i}>{i}</li>
        ))}
      </ul>
    </Card>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-stone-500">{label}</dt>
      <dd className="mt-0.5 text-sm">{children}</dd>
    </div>
  );
}

export function MedDetailPage() {
  const { slug } = useParams();
  const { medication: med, isPending } = useMedication(slug);
  const patient = usePatient();
  const protocols = useEffectiveProtocols();
  const { activeMembership, profile } = useAuth();
  const level = (activeMembership?.certification_level ?? profile?.certification_level) as ProviderLevel | null;

  if (isPending) return <Spinner />;
  if (!med) {
    return (
      <EmptyState title="Medication not in this formulary" action={<ButtonLink to="/meds">All medications</ButtonLink>}>
        It may not be enabled for your organization.
      </EmptyState>
    );
  }

  const results = calculateMedication(med, patient);
  const applicable = results.filter((r) => r.calculation.status !== 'not-applicable');
  const other = results.filter((r) => r.calculation.status === 'not-applicable');
  const relatedProtocols = (protocols.data ?? []).filter((p) => p.related_medications.includes(med.slug));

  return (
    <div>
      <Link to="/meds" className="mb-3 inline-flex text-sm text-stone-500 hover:text-stone-950 dark:text-stone-400 dark:hover:text-white">
        ← Medications
      </Link>
      <header className="mb-4">
        <h1 className="text-[1.75rem] font-semibold leading-tight tracking-tight">{med.genericName}</h1>
        {med.brandNames.length > 0 && <p className="text-stone-500">{med.brandNames.join(', ')}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <Badge tone="neutral">{med.drugClass}</Badge>
          <MedicationBadges med={med} level={level} />
        </div>
        {med.version !== undefined && (
          <p className="mt-1 text-xs text-stone-500">
            Monograph version {med.version}
            {med.updatedAt && ` · updated ${formatDate(med.updatedAt)}`}
            {med.formulary?.reviewed_at && ` · reviewed ${formatDate(med.formulary.reviewed_at)}`}
          </p>
        )}
      </header>

      <div className="space-y-4">
        {med.highAlert && (
          <Alert tone="danger" title="High-alert medication">
            Perform an independent double check of drug, concentration, dose, and volume before administration.
          </Alert>
        )}
        {med.isSample && !med.formulary?.reviewed_at && <SampleContentNotice what="monograph" />}
        {med.formulary?.local_notes && (
          <Alert tone="info" title="Agency note">
            {med.formulary.local_notes}
          </Alert>
        )}

        <PatientBar />

        <section>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-lg font-semibold">Dosing</h2>
            <ButtonLink size="sm" variant="ghost" to={`/tools/dosing?med=${med.slug}`} icon={<Syringe className="size-4" />}>
              Open in calculator
            </ButtonLink>
          </div>
          <div className="space-y-2">
            {applicable.length === 0 && <p className="text-sm text-stone-500">No dose rules apply to this patient.</p>}
            {applicable.map((r) => (
              <DoseCard key={r.rule.id} medication={med} rule={r.rule} calculation={r.calculation} />
            ))}
            {other.length > 0 && (
              <details className="rounded-md border border-stone-200 p-3 dark:border-stone-800">
                <summary className="cursor-pointer text-sm font-medium text-stone-600 dark:text-stone-300">Other populations ({other.length})</summary>
                <div className="mt-3 space-y-2">
                  {other.map((r) => (
                    <DoseCard key={r.rule.id} medication={med} rule={r.rule} calculation={r.calculation} muted />
                  ))}
                </div>
              </details>
            )}
          </div>
        </section>

        {med.concentrations.length > 0 && (
          <Card className="p-4">
            <h3 className="mb-2 font-semibold">Supplied as</h3>
            <ul className="flex flex-wrap gap-2">
              {med.concentrations.map((c) => (
                <li key={describeConcentration(c)}>
                  <Badge tone="neutral" className="px-2 py-1 text-sm">
                    {describeConcentration(c)}
                  </Badge>
                </li>
              ))}
            </ul>
          </Card>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <InfoList title="Indications" items={med.indications} />
          <InfoList title="Contraindications" items={med.contraindications} tone="danger" />
          <InfoList title="Precautions" items={med.precautions} tone="warning" />
          <InfoList title="Side effects" items={med.sideEffects} />
        </div>

        <Card className="p-4">
          <dl className="grid gap-4 sm:grid-cols-2">
            {med.mechanism && <Fact label="Mechanism">{med.mechanism}</Fact>}
            {med.onset && <Fact label="Onset">{med.onset}</Fact>}
            {med.duration && <Fact label="Duration">{med.duration}</Fact>}
            {med.pregnancy && <Fact label="Pregnancy">{med.pregnancy}</Fact>}
            {med.scope.length > 0 && <Fact label="Typical scope">{med.scope.map((l) => LEVEL_LABELS[l] ?? l).join(', ')}</Fact>}
          </dl>
          {med.notes && <Markdown className="mt-4 text-sm">{med.notes}</Markdown>}
        </Card>

        {relatedProtocols.length > 0 && (
          <Card className="p-4">
            <h3 className="mb-2 font-semibold">Referenced in protocols</h3>
            <ul className="space-y-1">
              {relatedProtocols.map((p) => (
                <li key={p.id}>
                  <Link to={`/protocols/${p.id}`} className="inline-flex items-center gap-2 text-sm font-medium text-stone-950 underline-offset-[3px] hover:underline dark:text-white">
                    <BookOpen className="size-4" /> {p.code ? `${p.code} ` : ''}
                    {p.title}
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
      <ClinicalDisclaimer />
    </div>
  );
}
